import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Crown, Shield, MessageSquare, Volume2, VolumeX, ChevronRight, Loader2, LogOut, Camera, X, Check, Edit2, Key, Music, FileText, Scale, Bell, BellOff, Smartphone, Building2, Eye } from 'lucide-react';
import { apiGet, apiPost, apiPut, clearToken, getToken } from '@/utils/api';
import { getCelebrationSound, setCelebrationSound, CELEBRATION_SOUND_OPTIONS, playSound } from '@/utils/audio';
import { 
  isPushSupported, 
  getNotificationPermission, 
  setupPushNotifications,
  getNotificationStatus,
  unregisterPushNotifications
} from '@/utils/pushNotifications';
import LinkedAccountsManager from '@/components/LinkedAccountsManager';
import AccessibilitySettings from '@/components/AccessibilitySettings';

const API = process.env.REACT_APP_BACKEND_URL;

function Profile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundEnabled') !== 'false';
  });
  
  // Sound selection state
  const [celebrationSound, setCelebrationSoundState] = useState(() => {
    return getCelebrationSound();
  });
  const [showSoundPicker, setShowSoundPicker] = useState(false);
  
  // Email change state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Avatar upload state
  const [avatarLoading, setAvatarLoading] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushDevices, setPushDevices] = useState(0);
  const [pushLoading, setPushLoading] = useState(false);
  
  // Push notification preferences (granular controls)
  const [pushPreferences, setPushPreferences] = useState({
    push_enabled: true,
    bill_reminders: true,
    envelope_prompts: true,
    daily_quotes: true,
    weekly_reports: true,
    budget_alerts: true,
    achievement_badges: true,
    household_updates: true
  });
  const [prefLoading, setPrefLoading] = useState(false);

  useEffect(() => {
    fetchUserData();
    checkPushNotificationStatus();
    fetchPushPreferences();
  }, []);

  const fetchUserData = async () => {
    try {
      // Fetch user info
      const userRes = await apiGet('/auth/me');
      if (userRes.ok) {
        const userData = await userRes.json();
        setUser(userData);
      }

      // Fetch subscription status
      const token = localStorage.getItem('session_token');
      const subRes = await fetch(`${API}/api/subscription/status`, {
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (subRes.ok) {
        const subData = await subRes.json();
        setSubscription(subData);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('soundEnabled', newValue.toString());
  };

  const handleSoundSelection = (soundId) => {
    setCelebrationSound(soundId);
    setCelebrationSoundState(soundId);
    // Play a preview of the selected sound
    const soundOption = CELEBRATION_SOUND_OPTIONS.find(opt => opt.id === soundId);
    if (soundOption) {
      playSound(soundOption.sound, 0.6);
    }
    setShowSoundPicker(false);
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('session_token');
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearToken();
      navigate('/');
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    
    setEmailLoading(true);
    setEmailError('');
    
    try {
      const res = await apiPost('/auth/change-email', {
        new_email: newEmail.trim(),
        password: emailPassword || null
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to change email');
      }
      
      // Update local user state
      setUser(prev => ({ ...prev, email: data.new_email }));
      setShowEmailModal(false);
      setNewEmail('');
      setEmailPassword('');
    } catch (error) {
      setEmailError(error.message);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }
    
    // Validate file size (500KB max)
    if (file.size > 500 * 1024) {
      alert('Image too large. Max 500KB.');
      return;
    }
    
    setAvatarLoading(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target?.result;
        
        try {
          const res = await apiPost('/auth/update-avatar', {
            avatar_url: base64
          });
          
          if (res.ok) {
            const data = await res.json();
            setUser(prev => ({ ...prev, picture: data.picture }));
          } else {
            const error = await res.json();
            alert(error.detail || 'Failed to upload avatar');
          }
        } catch (error) {
          console.error('Avatar upload error:', error);
          alert('Failed to upload avatar');
        } finally {
          setAvatarLoading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('File read error:', error);
      setAvatarLoading(false);
    }
  };

  const getSubscriptionLabel = () => {
    if (!subscription) return 'Free';
    if (subscription.lifetime_free) return 'Family Plan (Lifetime)';
    if (subscription.status === 'active') {
      const tierLabels = {
        monthly: 'Monthly Plan',
        annual: 'Annual Plan',
        family: 'Family Plan'
      };
      return tierLabels[subscription.tier] || 'Premium';
    }
    if (subscription.status === 'past_due') return 'Past Due';
    return 'Free';
  };

  const checkPushNotificationStatus = async () => {
    // Check browser support
    const supported = isPushSupported();
    setPushSupported(supported);
    
    if (!supported) return;
    
    // Check browser permission
    const permission = getNotificationPermission();
    setPushPermission(permission);
    
    // Check backend status
    const token = getToken();
    if (token) {
      const status = await getNotificationStatus(token);
      setPushEnabled(status.notifications_enabled);
      setPushDevices(status.active_devices);
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    
    const token = getToken();
    if (!token) {
      setPushLoading(false);
      return;
    }
    
    const result = await setupPushNotifications(token);
    
    if (result.success) {
      setPushEnabled(true);
      setPushPermission('granted');
      await checkPushNotificationStatus();
    } else if (result.error?.includes('denied')) {
      setPushPermission('denied');
    }
    
    setPushLoading(false);
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    
    const token = getToken();
    if (token) {
      await unregisterPushNotifications(token);
    }
    
    setPushEnabled(false);
    setPushDevices(0);
    setPushLoading(false);
  };

  // Fetch push notification preferences from backend
  const fetchPushPreferences = async () => {
    try {
      const res = await apiGet('/settings/push-preferences');
      if (res.ok) {
        const data = await res.json();
        setPushPreferences(data);
      }
    } catch (error) {
      console.error('Error fetching push preferences:', error);
    }
  };

  // Update a single push preference
  const updatePushPreference = async (key, value) => {
    const newPrefs = { ...pushPreferences, [key]: value };
    setPushPreferences(newPrefs);
    setPrefLoading(true);
    
    try {
      const res = await apiPut('/settings/push-preferences', newPrefs);
      if (!res.ok) {
        // Revert on error
        setPushPreferences(pushPreferences);
        console.error('Failed to update push preference');
      }
    } catch (error) {
      // Revert on error
      setPushPreferences(pushPreferences);
      console.error('Error updating push preference:', error);
    } finally {
      setPrefLoading(false);
    }
  };

  const getNextBillingDate = () => {
    if (!subscription?.expires_at) return null;
    return new Date(subscription.expires_at).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isGoogleUser = user?.google_id != null;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a2634] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00d4ff] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a2634] flex flex-col" role="main" aria-label="Profile settings">
      {/* Hidden file input for avatar upload */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleAvatarUpload}
        accept="image/*"
        className="hidden"
        aria-label="Upload avatar image"
        id="avatar-upload"
      />

      {/* Header */}
      <header className="bg-[#1a2634] border-b border-[#2a3a4a] px-4 py-4 flex items-center">
        <button
          onClick={() => navigate(-1)}
          className="text-white mr-4 p-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00d4ff]"
          data-testid="back-button"
          aria-label="Go back"
        >
          <ArrowLeft className="w-6 h-6" aria-hidden="true" />
        </button>
        <h1 className="text-xl font-bold text-white italic">Profile</h1>
      </header>

      <div className="flex-1 p-4 space-y-6 pb-32">
        {/* Profile Section */}
        <section className="flex flex-col items-center py-6" aria-label="Profile photo">
          {/* Avatar with upload button */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-200 to-pink-300 flex items-center justify-center overflow-hidden border-4 border-[#2a3a4a]">
              {avatarLoading ? (
                <Loader2 className="w-8 h-8 text-[#1a2634] animate-spin" aria-label="Loading" />
              ) : user?.picture ? (
                <img src={user.picture} alt={`${user?.name || 'User'}'s avatar`} className="w-full h-full object-cover" />
              ) : (
                <User className="w-12 h-12 text-[#1a2634]" aria-hidden="true" />
              )}
            </div>
            {/* Camera button overlay */}
            <button
              onClick={handleAvatarClick}
              className="absolute bottom-0 right-0 w-8 h-8 bg-[#00d4ff] rounded-full flex items-center justify-center border-2 border-[#1a2634] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              data-testid="change-avatar-btn"
              aria-label="Change profile photo"
            >
              <Camera className="w-4 h-4 text-[#1a2634]" aria-hidden="true" />
            </button>
          </div>
        </section>

        {/* User Info - Grouped Card */}
        <section className="bg-[#243447] rounded-xl overflow-hidden" aria-label="Account information">
          {/* Name */}
          <div className="p-4">
            <span className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-1 block" id="name-label">Name</span>
            <div className="text-white font-medium" aria-labelledby="name-label">{user?.name || 'Not set'}</div>
          </div>

          <div className="border-t border-white/10 mx-4" aria-hidden="true" />

          {/* Email - Clickable to change */}
          <button
            onClick={() => setShowEmailModal(true)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00d4ff]"
            data-testid="change-email-btn"
            aria-label={`Change email. Current email: ${user?.email || 'Not set'}`}
          >
            <div>
              <span className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-1 block">Email</span>
              <div className="text-white font-medium">{user?.email || 'Not set'}</div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-500" aria-hidden="true" />
          </button>

          {/* Change Password - only for non-Google users */}
          {!isGoogleUser && (
            <>
              <div className="border-t border-white/10 mx-4" aria-hidden="true" />
              <Link
                to={`/app/forgot-password?email=${encodeURIComponent(user?.email || '')}`}
                className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00d4ff]"
                data-testid="change-password-link"
                aria-label="Change your password"
              >
                <div>
                  <span className="text-gray-400 text-xs font-medium uppercase tracking-wide mb-1 block">Password</span>
                  <div className="text-white font-medium">Change your password</div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-500" aria-hidden="true" />
              </Link>
            </>
          )}
        </section>

        {/* Subscription Section */}
        <div className="pt-4">
          <h2 className="text-white font-bold text-lg mb-3">Subscription</h2>
          <Link
            to="/app/pricing"
            className="bg-[#243447] rounded-xl p-4 flex items-center justify-between"
            data-testid="manage-subscription-link"
          >
            <div>
              <div className="text-white font-bold">Manage subscription</div>
              <div className="text-gray-400 text-sm flex items-center gap-2">
                <Crown className="w-4 h-4 text-yellow-500" />
                {getSubscriptionLabel()}
                {getNextBillingDate() && (
                  <span className="text-gray-500">• Renews {getNextBillingDate()}</span>
                )}
              </div>
            </div>
            <ChevronRight className="w-6 h-6 text-gray-500" />
          </Link>

          {/* Family Management - only show for family plan owners */}
          {(subscription?.tier === 'family' || subscription?.lifetime_free) && !subscription?.family_owner_id && (
            <Link
              to="/app/family"
              className="bg-[#243447] rounded-xl p-4 flex items-center justify-between mt-3"
              data-testid="family-management-link"
            >
              <div>
                <div className="text-white font-bold">Family Management</div>
                <div className="text-gray-400 text-sm">Manage invite codes & family members</div>
              </div>
              <ChevronRight className="w-6 h-6 text-gray-500" />
            </Link>
          )}
        </div>

        {/* Preferences Section */}
        <div className="pt-4">
          <h2 className="text-white font-bold text-lg mb-3">Preferences</h2>
          
          {/* Sound Effects Toggle */}
          <div 
            className="bg-[#243447] rounded-xl p-4 flex items-center justify-between cursor-pointer"
            onClick={toggleSound}
            data-testid="sound-toggle"
          >
            <div className="flex items-center gap-3">
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-[#00d4ff]" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-500" />
              )}
              <div>
                <div className="text-white font-bold">Sound Effects</div>
                <div className="text-gray-400 text-sm">Cash register, celebrations, etc.</div>
              </div>
            </div>
            {/* Toggle Switch */}
            <div className={`w-12 h-7 rounded-full transition-colors ${soundEnabled ? 'bg-[#00d4ff]' : 'bg-gray-600'} relative`}>
              <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${soundEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </div>
          </div>

          {/* Celebration Sound Selection */}
          {soundEnabled && (
            <div className="mt-3">
              <div 
                className="bg-[#243447] rounded-xl p-4 flex items-center justify-between cursor-pointer"
                onClick={() => setShowSoundPicker(!showSoundPicker)}
                data-testid="celebration-sound-selector"
              >
                <div className="flex items-center gap-3">
                  <Music className="w-5 h-5 text-purple-500" />
                  <div>
                    <div className="text-white font-bold">Celebration Sound</div>
                    <div className="text-gray-400 text-sm">
                      {CELEBRATION_SOUND_OPTIONS.find(opt => opt.id === celebrationSound)?.name || 'Default'}
                    </div>
                  </div>
                </div>
                <ChevronRight className={`w-6 h-6 text-gray-500 transition-transform ${showSoundPicker ? 'rotate-90' : ''}`} />
              </div>

              {/* Sound Options Dropdown */}
              {showSoundPicker && (
                <div className="mt-2 bg-[#1a2634] rounded-xl overflow-hidden border border-[#2d4052]">
                  {CELEBRATION_SOUND_OPTIONS.map((option) => (
                    <div
                      key={option.id}
                      className={`p-4 flex items-center justify-between cursor-pointer transition-colors ${
                        celebrationSound === option.id 
                          ? 'bg-[#00d4ff]/10 border-l-4 border-[#00d4ff]' 
                          : 'hover:bg-[#243447] border-l-4 border-transparent'
                      }`}
                      onClick={() => handleSoundSelection(option.id)}
                      data-testid={`sound-option-${option.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">
                          {option.id === 'default' ? '💰' : option.id === 'dababy' ? '🎤' : '🔥'}
                        </span>
                        <div>
                          <div className="text-white font-medium">{option.name}</div>
                          <div className="text-gray-500 text-xs">Tap to preview & select</div>
                        </div>
                      </div>
                      {celebrationSound === option.id && (
                        <Check className="w-5 h-5 text-[#00d4ff]" />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notifications Section */}
        <div className="pt-4">
          <h2 className="text-white font-bold text-lg mb-3">Notifications</h2>
          
          {!pushSupported ? (
            // Browser doesn't support push notifications
            <div className="bg-[#243447] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <BellOff className="w-5 h-5 text-gray-500" />
                <div>
                  <div className="text-white font-bold">Push Notifications</div>
                  <div className="text-gray-400 text-sm">Not supported in this browser</div>
                </div>
              </div>
            </div>
          ) : pushPermission === 'denied' ? (
            // Permission was denied
            <div className="bg-[#243447] rounded-xl p-4">
              <div className="flex items-center gap-3">
                <BellOff className="w-5 h-5 text-red-400" />
                <div>
                  <div className="text-white font-bold">Push Notifications Blocked</div>
                  <div className="text-gray-400 text-sm">Enable in your browser settings to receive reminders</div>
                </div>
              </div>
            </div>
          ) : (
            // Normal state - can toggle
            <div className="bg-[#243447] rounded-xl overflow-hidden">
              {/* Push Notification Toggle */}
              <div 
                className="p-4 flex items-center justify-between cursor-pointer"
                onClick={pushEnabled ? handleDisablePush : handleEnablePush}
                data-testid="push-notification-toggle"
              >
                <div className="flex items-center gap-3">
                  {pushEnabled ? (
                    <Bell className="w-5 h-5 text-[#00d4ff]" />
                  ) : (
                    <BellOff className="w-5 h-5 text-gray-500" />
                  )}
                  <div>
                    <div className="text-white font-bold">Push Notifications</div>
                    <div className="text-gray-400 text-sm">
                      {pushEnabled 
                        ? 'Receive streak reminders & alerts' 
                        : 'Enable to get daily reminders'}
                    </div>
                  </div>
                </div>
                {pushLoading ? (
                  <Loader2 className="w-5 h-5 text-[#00d4ff] animate-spin" />
                ) : (
                  <div className={`w-12 h-7 rounded-full transition-colors ${pushEnabled ? 'bg-[#00d4ff]' : 'bg-gray-600'} relative`}>
                    <div className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${pushEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                  </div>
                )}
              </div>

              {/* Active Devices Info */}
              {pushEnabled && pushDevices > 0 && (
                <>
                  <div className="border-t border-white/10 mx-4" />
                  <div className="p-4 flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-green-500" />
                    <div>
                      <div className="text-white font-medium">Active Devices</div>
                      <div className="text-gray-400 text-sm">
                        {pushDevices} device{pushDevices !== 1 ? 's' : ''} receiving notifications
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Notification Types - Granular Controls */}
          {pushEnabled && (
            <div className="mt-3 bg-[#1a2634] rounded-xl border border-[#2d4052] p-4">
              <div className="text-gray-400 text-sm mb-3 font-medium">Notification Preferences</div>
              
              {/* Master Toggle */}
              <div className="flex items-center justify-between py-2 border-b border-white/10 mb-2">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-[#00d4ff]" />
                  <span className="text-white text-sm font-medium">All Notifications</span>
                </div>
                <button
                  onClick={() => updatePushPreference('push_enabled', !pushPreferences.push_enabled)}
                  disabled={prefLoading}
                  className="relative"
                  data-testid="toggle-push-master"
                >
                  <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.push_enabled ? 'bg-[#00d4ff]' : 'bg-gray-600'}`}>
                    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.push_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </button>
              </div>
              
              {/* Individual Toggles */}
              <div className={`space-y-3 ${!pushPreferences.push_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {/* Bill Reminders */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💰</span>
                    <span className="text-gray-300 text-sm">Bill Reminders</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('bill_reminders', !pushPreferences.bill_reminders)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-bills"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.bill_reminders ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.bill_reminders ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Envelope Challenges */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📨</span>
                    <span className="text-gray-300 text-sm">Envelope Challenges</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('envelope_prompts', !pushPreferences.envelope_prompts)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-envelope"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.envelope_prompts ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.envelope_prompts ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Daily Quotes */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">💡</span>
                    <span className="text-gray-300 text-sm">Daily Quotes</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('daily_quotes', !pushPreferences.daily_quotes)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-quotes"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.daily_quotes ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.daily_quotes ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Budget Alerts */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📊</span>
                    <span className="text-gray-300 text-sm">Budget Alerts</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('budget_alerts', !pushPreferences.budget_alerts)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-budget"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.budget_alerts ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.budget_alerts ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Achievement Badges */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🏆</span>
                    <span className="text-gray-300 text-sm">Achievement Badges</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('achievement_badges', !pushPreferences.achievement_badges)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-badges"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.achievement_badges ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.achievement_badges ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Household Updates */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">👨‍👩‍👧‍👦</span>
                    <span className="text-gray-300 text-sm">Household Updates</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('household_updates', !pushPreferences.household_updates)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-household"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.household_updates ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.household_updates ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
                
                {/* Weekly Reports */}
                <div className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📈</span>
                    <span className="text-gray-300 text-sm">Weekly Reports</span>
                  </div>
                  <button
                    onClick={() => updatePushPreference('weekly_reports', !pushPreferences.weekly_reports)}
                    disabled={prefLoading}
                    className="relative"
                    data-testid="toggle-push-reports"
                  >
                    <div className={`w-10 h-6 rounded-full transition-colors ${pushPreferences.weekly_reports ? 'bg-green-500' : 'bg-gray-600'}`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${pushPreferences.weekly_reports ? 'translate-x-4' : 'translate-x-0.5'}`} />
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Linked Bank Accounts Section */}
        <div className="pt-4">
          <h2 className="text-white font-bold text-lg mb-3">Bank Connections</h2>
          <LinkedAccountsManager />
        </div>

        {/* Accessibility Settings Section */}
        <div className="pt-4">
          <div className="bg-[#243447] rounded-xl p-4">
            <AccessibilitySettings />
          </div>
        </div>

        {/* Links Section - Grouped Card */}
        <div className="pt-4">
          <h2 className="text-white font-bold text-lg mb-3">Support & Legal</h2>
          <div className="bg-[#243447] rounded-xl overflow-hidden">
            {/* Security & Encryption */}
            <Link
              to="/app/security"
              className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="security-link"
            >
              <div className="flex items-center gap-3">
                <Shield className="w-5 h-5 text-green-500" />
                <span className="text-white font-medium">Security & Encryption</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </Link>

            <div className="border-t border-white/10 mx-4" />

            {/* Give Feedback */}
            <Link
              to="/app/contact"
              className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="feedback-link"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className="w-5 h-5 text-purple-500" />
                <span className="text-white font-medium">Give Feedback</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </Link>

            <div className="border-t border-white/10 mx-4" />

            {/* Privacy Policy */}
            <Link
              to="/privacy"
              className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="privacy-link"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-gray-400" />
                <span className="text-white font-medium">Privacy Policy</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </Link>

            <div className="border-t border-white/10 mx-4" />

            {/* Terms */}
            <Link
              to="/privacy"
              className="p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
              data-testid="terms-link"
            >
              <div className="flex items-center gap-3">
                <Scale className="w-5 h-5 text-gray-400" />
                <span className="text-white font-medium">Terms of Service</span>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-500" />
            </Link>
          </div>
        </div>

        {/* Account Actions */}
        <div className="pt-4">
          {/* Logout */}
          <button
            onClick={handleLogout}
            className="w-full bg-[#243447] rounded-xl p-4 flex items-center justify-center gap-2 text-red-400 font-bold hover:bg-[#2d4052] transition-colors"
            data-testid="logout-button"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>

          {/* Delete Account - Simple text link */}
          <div className="text-center mt-4">
            <Link
              to="/app/security"
              className="text-[#ff4d4d] text-sm hover:underline"
              data-testid="delete-account-link"
            >
              Delete Account
            </Link>
          </div>
        </div>

        {/* App Version */}
        <div className="text-center pt-6">
          <p className="text-gray-600 text-sm">GuapGoals v2.0</p>
        </div>
      </div>

      {/* Email Change Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#1a2634] border border-[#2a3a4a] rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">Change Email</h2>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setNewEmail('');
                  setEmailPassword('');
                  setEmailError('');
                }}
                className="text-gray-500 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={handleEmailChange} className="space-y-4">
              <div>
                <label className="text-white font-bold text-sm mb-2 block">Current Email</label>
                <div className="text-gray-400 bg-[#243447] rounded-lg px-4 py-3">{user?.email}</div>
              </div>
              
              <div>
                <label className="text-white font-bold text-sm mb-2 block">New Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  className="w-full bg-[#243447] border border-[#3a4a5a] rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00d4ff]"
                  data-testid="new-email-input"
                  required
                />
              </div>
              
              {!isGoogleUser && (
                <div>
                  <label className="text-white font-bold text-sm mb-2 block">Password</label>
                  <input
                    type="password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full bg-[#243447] border border-[#3a4a5a] rounded-lg px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-[#00d4ff]"
                    data-testid="email-password-input"
                    required
                  />
                  <p className="text-gray-500 text-xs mt-1">Password required to verify your identity</p>
                </div>
              )}
              
              {emailError && (
                <div className="text-red-400 text-sm bg-red-400/10 px-4 py-2 rounded-lg">
                  {emailError}
                </div>
              )}
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEmailModal(false);
                    setNewEmail('');
                    setEmailPassword('');
                    setEmailError('');
                  }}
                  className="flex-1 py-3 bg-[#243447] text-white rounded-xl font-bold hover:bg-[#2a3a4a] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={emailLoading || !newEmail.trim()}
                  className="flex-1 py-3 bg-[#00d4ff] text-[#1a2634] rounded-xl font-bold hover:bg-[#00d4ff]/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="confirm-email-change-btn"
                >
                  {emailLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4" />
                      Update
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile;
